import axios, {
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosHeaders
} from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ConfigConstants } from './config';
import { imageRevisionParam } from './imageRevisions';
import { RefreshTokenResponse, Token } from './types';

const axiosInstance = axios.create({
  baseURL: `${ConfigConstants.baseUrl}`
});

const refreshToken = async (): Promise<RefreshTokenResponse> => {
  try {
    const token = JSON.parse(localStorage.getItem('token') || '{}') as Token;
    const response = await axios.post(`${ConfigConstants.baseUrl}token`, {
      refreshToken: token.refreshToken,
      grantType: 'refresh_token'
    });
    return response.data;
  } catch (error) {
    console.error('Error refreshing token:', error);
    throw error;
  }
};

axiosInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const revision = imageRevisionParam(config.method, config.url);
  if (revision != null) {
    if (config.params instanceof URLSearchParams) {
      config.params.set('v', String(revision));
    } else {
      config.params = {
        ...(config.params as Record<string, string | number | boolean | undefined> | undefined),
        v: revision
      };
    }
  }
  return config;
});

axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = JSON.parse(localStorage.getItem('token') || '{}') as Token;

    if (token.accessToken) {
      const headers = new AxiosHeaders(config.headers);
      headers.set('Authorization', `Bearer ${token.accessToken}`);
      config.headers = headers;
    }
    return config;
  },
  (error: AxiosError) => {
    console.error('Request error:', error);
    toast.error('Request error: ' + error.message, {
      position: 'top-center',
      autoClose: 3500,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'light'
    });
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      try {
        const newToken = await refreshToken();
        localStorage.setItem('token', JSON.stringify(newToken));
        const headers = new AxiosHeaders(originalRequest.headers);
        headers.set('Authorization', `Bearer ${newToken.accessToken}`);
        originalRequest.headers = headers;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.error('Error during token refresh:', refreshError);
        return Promise.reject(refreshError);
      }
    }

    if (error.response && error.response.status === 500) {
      console.error('Internal Server Error:', error.response);
      toast.error('Internal Server Error: Please try again later.', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined
      });
    }

    console.error('Response error:', error);
    return Promise.reject(error);
  }
);

export default axiosInstance;
